"use client";

/**
 * Shares the session planner card's live state with the session finder in the spot page's
 * "Better nearby" block. The card owns the session data (which sports this place can plan),
 * the account-synced preferences and the reader's edits; the finder renders from exactly that
 * state, so a preference change in "Plan my session" reaches the finder at once and the finder
 * never offers a sport the place cannot plan.
 *
 * Published only from effects in the browser, so the server snapshot is always null and the
 * finder never renders into the initial HTML (no hydration mismatch).
 */
import { useCallback, useSyncExternalStore } from "react";
import type { PlannerSport, SessionPreferences } from "@/lib/session-planner/types";

export type PlannerFinderState = Readonly<{
  slug: string;
  sport: PlannerSport;
  prefs: SessionPreferences;
  sportUrlKey: string;
}>;

let current: PlannerFinderState | null = null;
const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) listener();
}

/** Called by SessionPlannerCard whenever its finder-relevant state changes. */
export function publishPlannerFinderState(next: PlannerFinderState): void {
  current = next;
  emit();
}

/** Clears the state for one place (the card unmounted or has nothing the finder can use). */
export function clearPlannerFinderState(slug: string): void {
  if (current?.slug !== slug) return;
  current = null;
  emit();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

const serverSnapshot = (): PlannerFinderState | null => null;

/** The planner state for this place, or null until the card has loaded it. */
export function usePlannerFinderState(slug: string): PlannerFinderState | null {
  const getSnapshot = useCallback(() => (current?.slug === slug ? current : null), [slug]);
  return useSyncExternalStore(subscribe, getSnapshot, serverSnapshot);
}
